/**
 * <AgentControlPanel /> — per-agent command surface in /agents.
 * Phase 13 U4 #296.
 *
 * Shows the available realtime commands as clickable buttons, with the
 * busy / result feedback for each. Commands route through useAgentSocketContext.
 *
 * Sensitive commands (those that mutate D1 or send messages) get a
 * confirm prompt before executing.
 */

import { useState } from "react";
import { Zap, Loader2, Check, AlertTriangle } from "lucide-react";
import { useAgentSocketContext } from "../hooks/useAgentSocketContext";

interface CommandSpec {
  type: string;
  label: string;
  description?: string;
  sensitive?: boolean;
  fields?: Array<{ name: string; placeholder: string }>;
}

interface AgentControlPanelProps {
  agentSlug: string;
  title: string;
  commands: CommandSpec[];
}

type Status = "idle" | "executing" | "done" | "error";

export function AgentControlPanel({ agentSlug, title, commands }: AgentControlPanelProps) {
  const { sendCommand, state: wsState } = useAgentSocketContext();
  const [statusByCmd, setStatusByCmd] = useState<Record<string, Status>>({});
  const [lastResult, setLastResult] = useState<string | null>(null);

  const run = async (spec: CommandSpec) => {
    let payload: Record<string, unknown> | undefined;
    if (spec.fields && spec.fields.length > 0) {
      payload = {};
      for (const f of spec.fields) {
        const v = window.prompt(f.placeholder);
        if (v === null) return;
        payload[f.name] = v;
      }
    }
    if (spec.sensitive) {
      const ok = window.confirm(`Confirmer la commande "${spec.label}" ?`);
      if (!ok) return;
    }
    setStatusByCmd((s) => ({ ...s, [spec.type]: "executing" }));
    setLastResult(null);
    try {
      const result = await sendCommand(spec.type, payload);
      setStatusByCmd((s) => ({ ...s, [spec.type]: "done" }));
      setLastResult(`${spec.type} → done · ${JSON.stringify(result).slice(0, 200)}`);
    } catch (err) {
      setStatusByCmd((s) => ({ ...s, [spec.type]: "error" }));
      setLastResult(`${spec.type} → error · ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const disabled = wsState !== "connected";

  return (
    <div className="panel p-4 sm:p-5 space-y-3">
      <div className="flex items-center justify-between">
        <p className="eyebrow">{title}</p>
        <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
          {agentSlug}
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {commands.map((c) => {
          const status = statusByCmd[c.type] ?? "idle";
          const Icon = status === "executing" ? Loader2 : status === "done" ? Check : status === "error" ? AlertTriangle : Zap;
          return (
            <button
              key={c.type}
              type="button"
              onClick={() => void run(c)}
              disabled={disabled || status === "executing"}
              title={c.description ?? c.type}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded font-mono text-[11px] uppercase tracking-wider border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                color: status === "error" ? "var(--color-status-error)" : "var(--color-accent-glow)",
                borderColor: status === "error" ? "rgba(239,68,68,0.45)" : "var(--color-accent)",
                background: "var(--color-accent-soft)",
              }}
            >
              <Icon className={`size-3.5 ${status === "executing" ? "animate-spin" : ""}`} strokeWidth={2} aria-hidden="true" />
              {c.label}
            </button>
          );
        })}
      </div>
      {lastResult && (
        <p className="font-mono text-[10px] text-[var(--color-text-muted)] break-words">
          {lastResult}
        </p>
      )}
      {disabled && (
        <p className="font-mono text-[10px] text-[var(--color-text-dim)]">
          WebSocket {wsState} — commandes désactivées jusqu'à reconnexion.
        </p>
      )}
    </div>
  );
}

/**
 * Curated catalog of all realtime commands the dashboard exposes today.
 * Adding a command : add an entry here AND register the route in
 * SwarmFeedDO.route() (#289-292).
 */
export const COMMAND_CATALOG: Array<{
  agentSlug: string;
  title: string;
  commands: CommandSpec[];
}> = [
  {
    agentSlug: "marketing",
    title: "Marketing",
    commands: [
      { type: "marketing.force_heartbeat", label: "Force heartbeat", description: "Run the 4h heartbeat now" },
      {
        type: "marketing.pause_sequence_for_lead",
        label: "Pause sequence",
        description: "Pause every active sequence run for a lead",
        sensitive: true,
        fields: [{ name: "leadId", placeholder: "Lead ID" }, { name: "reason", placeholder: "Reason (optional)" }],
      },
      {
        type: "marketing.resume_sequence_for_lead",
        label: "Resume sequence",
        description: "Resume paused sequences for a lead",
        fields: [{ name: "leadId", placeholder: "Lead ID" }],
      },
    ],
  },
  {
    agentSlug: "support",
    title: "Support",
    commands: [
      { type: "support.force_heartbeat", label: "Force heartbeat" },
      {
        type: "support.approve_reply",
        label: "Approve draft",
        description: "Approve + send an auto-reply draft",
        sensitive: true,
        fields: [{ name: "draftId", placeholder: "Draft ID" }],
      },
      {
        type: "support.reject_reply",
        label: "Reject draft",
        fields: [{ name: "draftId", placeholder: "Draft ID" }],
      },
    ],
  },
  {
    agentSlug: "growth",
    title: "Growth",
    commands: [
      { type: "growth.force_heartbeat", label: "Force heartbeat" },
    ],
  },
  {
    agentSlug: "ceo",
    title: "CEO",
    commands: [
      { type: "ceo.force_heartbeat", label: "Force heartbeat" },
    ],
  },
  {
    agentSlug: "verifier",
    title: "Verifier",
    commands: [
      { type: "verifier.force_heartbeat", label: "Force heartbeat" },
    ],
  },
  {
    agentSlug: "integrator",
    title: "Integrator",
    commands: [
      { type: "integrator.force_heartbeat", label: "Force heartbeat" },
    ],
  },
  {
    agentSlug: "engineering",
    title: "Engineering",
    commands: [
      { type: "engineering.force_heartbeat", label: "Force heartbeat" },
    ],
  },
  {
    agentSlug: "fundraising",
    title: "Fundraising",
    commands: [
      { type: "fundraising.force_heartbeat", label: "Force heartbeat" },
    ],
  },
];
